import type {
  WorkflowFormFieldInputFiles,
  WorkflowFormFieldInputPhotos,
} from '@workflows/types';
import type {
  WorkflowFieldFileOrigin,
  WorkflowFileConstraints,
  WorkflowFileOperation,
  WorkflowSelectedFilesCheck,
} from './workflow-file-field.types';

/**
 * Лимит размера файла поля регламента, если в схеме не задан maxFileSizeBytes.
 * Совпадает с WORKFLOW_DEFAULT_MAX_FILE_SIZE_BYTES backend (лимит загрузки на статику);
 * значение из схемы больше лимита backend не поднимает.
 */
export const WORKFLOW_DEFAULT_MAX_FILE_SIZE_BYTES = 50_000_000;

const fileOrigins = new WeakMap<File, WorkflowFieldFileOrigin>();
const fileOperationIds: Record<WorkflowFileOperation, WeakMap<File, string>> = {
  upload: new WeakMap<File, string>(),
  remove: new WeakMap<File, string>(),
};

export function setWorkflowFileOrigin(
  file: File,
  origin: WorkflowFieldFileOrigin,
): void {
  fileOrigins.set(file, origin);
}

export function getWorkflowFileOrigin(
  file: File,
): WorkflowFieldFileOrigin | undefined {
  return fileOrigins.get(file);
}

/**
 * Постоянный operationId операции с файлом: повтор после временного сбоя
 * не создаст дубликат на сервере. У загрузки и удаления — разные id.
 */
export function getWorkflowFileOperationId(
  file: File,
  operation: WorkflowFileOperation,
): string {
  let operationId = fileOperationIds[operation].get(file);

  if (!operationId) {
    operationId = crypto.randomUUID();
    fileOperationIds[operation].set(file, operationId);
  }

  return operationId;
}

/**
 * Сбросить operationId после окончательной ошибки: сервер хранит её результат,
 * и следующая попытка с тем же id вернула бы ту же ошибку
 */
export function resetWorkflowFileOperationId(
  file: File,
  operation: WorkflowFileOperation,
): void {
  fileOperationIds[operation].delete(file);
}

/**
 * MIME подходит под разрешённый: точное совпадение или маска вида `image/*` (как на backend)
 */
export function isWorkflowMimetypeAllowed(
  mimetype: string,
  allowedMimetypes: readonly string[],
): boolean {
  const normalized = mimetype.toLowerCase();

  return allowedMimetypes.some(allowed => {
    const allowedNormalized = allowed.trim().toLowerCase();

    if (allowedNormalized.endsWith('/*')) {
      return normalized.startsWith(allowedNormalized.slice(0, -1));
    }

    return normalized === allowedNormalized;
  });
}

export function getWorkflowFileConstraints(
  field: WorkflowFormFieldInputFiles | WorkflowFormFieldInputPhotos,
  defaultAccept = '',
): WorkflowFileConstraints {
  const validation = field.validation;
  const allowedMimetypes = Array.isArray(validation?.allowedFilesMimetypes)
    ? validation.allowedFilesMimetypes.filter(Boolean)
    : [];
  const schemaMaxSize = validation?.maxFileSizeBytes;
  const maxFileSizeBytes =
    typeof schemaMaxSize === 'number' && schemaMaxSize > 0
      ? Math.min(schemaMaxSize, WORKFLOW_DEFAULT_MAX_FILE_SIZE_BYTES)
      : WORKFLOW_DEFAULT_MAX_FILE_SIZE_BYTES;
  const maxFilesCount =
    typeof validation?.maxFilesCount === 'number' &&
    validation.maxFilesCount > 0
      ? validation.maxFilesCount
      : undefined;

  return {
    allowedMimetypes,
    accept:
      allowedMimetypes.length > 0 ? allowedMimetypes.join(',') : defaultAccept,
    maxFileSizeBytes,
    maxFilesCount,
  };
}

/**
 * Проверить выбранные файлы до загрузки: тип, размер и общее количество в поле
 */
export function checkSelectedWorkflowFiles(
  files: readonly File[],
  constraints: WorkflowFileConstraints,
  currentCount: number,
): WorkflowSelectedFilesCheck {
  const result: WorkflowSelectedFilesCheck = { accepted: [], rejected: [] };

  for (const file of files) {
    if (
      constraints.allowedMimetypes.length > 0 &&
      !isWorkflowMimetypeAllowed(file.type, constraints.allowedMimetypes)
    ) {
      result.rejected.push({ file, reason: 'mimetype' });
      continue;
    }

    if (file.size > constraints.maxFileSizeBytes) {
      result.rejected.push({ file, reason: 'size' });
      continue;
    }

    if (
      constraints.maxFilesCount !== undefined &&
      currentCount + result.accepted.length >= constraints.maxFilesCount
    ) {
      result.rejected.push({ file, reason: 'count' });
      continue;
    }

    result.accepted.push(file);
  }

  return result;
}

/**
 * Файлы из значения контрола поля файлов: Taiga хранит один File
 * в одиночном режиме (maxFilesCount: 1) и массив — в множественном
 */
export function getWorkflowControlFiles(value: unknown): File[] {
  if (value instanceof File) {
    return [value];
  }

  return Array.isArray(value)
    ? value.filter((item): item is File => item instanceof File)
    : [];
}

/**
 * Значение контрола в формате режима поля: один File (или null) либо массив (или null)
 */
export function toWorkflowControlValue(
  files: readonly File[],
  multiple: boolean,
): File | File[] | null {
  if (!multiple) {
    return files[0] ?? null;
  }

  return files.length > 0 ? [...files] : null;
}

const MIMETYPES_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  zip: 'application/zip',
  rar: 'application/x-rar-compressed',
  txt: 'text/plain',
  csv: 'text/csv',
};

export function getWorkflowMimetypeFromFilename(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase() ?? '';

  return MIMETYPES_BY_EXTENSION[extension] ?? 'application/octet-stream';
}
